<template>
  <div
    data-test="account-application-page"
    class="flex flex-col items-center h-full -mt-8 bg-white md:flex-row md:-mt-20 md:items-stretch md:justify-evenly"
  >
    <LazyDynamicForm
      v-if="dynamicForm"
      ref="dynamicFormRef"
      class="p-6 border-b-2 border-gray-300 border-solid md:w-1/3 md:py-11 lg:px-11 md:border-r-2 md:border-b-0"
      :form-entry="dynamicForm"
      :submit-button-text="t('submit_account_application')"
      :classes="classes"
      :is-loading="isLoading"
      :service-validation-errors="serviceValidationErrors"
      @submit="onSubmit"
      @reset-error="resetErrorByType"
    />

    <LazyAccountApplicationInfo class="px-6 md:w-2/3 py-11 lg:px-11" :application-info="applicationInfo" />
  </div>
</template>

<script setup lang="ts">
import { DynamicForm } from '#build/components';
import { useUiStore } from '@/stores';
import gql from '@/gql/contentful';

type ContentFulEntry = {
  fields?: {
    bannerBackgroundTemplate?: string;
    bannerButtonText?: string;
    bannerButtonUrl?: string;
    bannerChecklist?: string[];
    title?: string;
    image?: string;
    dynamicForm: {
      sys: {
        id: string;
      };
    };
    descriptionTitle?: string;
    description?: {
      content: [
        {
          content: [
            {
              value: string;
            },
          ];
        },
      ];
    };
  };
};

type Props = {
  content?: ContentFulEntry;
};

const props = withDefaults(defineProps<Props>(), {
  content: undefined,
});

const { $authService } = useNuxtApp();
const { t } = useI18n();

const classes = {
  title: 'font-semibold text-primary-900 text-base',
  description: 'text-gray-900 font-thin text-sm py-2',
  button: 'self-start px-12 py-5',
};

const dynamicFormRef = ref<InstanceType<typeof DynamicForm> | null>(null);
const dynamicForm = ref<unknown | null>(null);
const isLoading = ref(false);
const serviceValidationErrors = ref<{ [key: string]: string }[]>([]);

const { addToastMessage } = useUiStore();
const { fetchEntryWithGraphQL } = useContentful();

const { data } = await useAsyncData('accountApplicationEntry', async () => {
  const id = props.content?.fields?.dynamicForm.sys.id;

  if (!id) return;

  const { data } = await fetchEntryWithGraphQL(gql.getDynamicForm, {
    id,
  });

  return data;
});

if (data.value) {
  dynamicForm.value = data.value.dynamicForm;
}

const applicationInfo = computed(() => {
  return {
    bannerBackgroundTemplate: props.content?.fields?.bannerBackgroundTemplate,
    bannerButtonText: props.content?.fields?.bannerButtonText,
    bannerButtonUrl: props.content?.fields?.bannerButtonUrl,
    bannerChecklist: props.content?.fields?.bannerChecklist,
    description: props.content?.fields?.description?.content[0].content[0].value,
    title: props.content?.fields?.title,
    image: props.content?.fields?.image,
    descriptionTitle: props.content?.fields?.descriptionTitle,
  };
});

const resetAllErrors = () => {
  serviceValidationErrors.value = [];
};

const resetErrorByType = (errorType: string) => {
  serviceValidationErrors.value = serviceValidationErrors.value.filter((error) => error.key !== errorType);
};

const onSubmit = async (submitData: FormData) => {
  isLoading.value = true;
  resetAllErrors();
  const { error } = await $authService.sendSignupEmail(submitData, { useCustomErrorHandling: true });
  if (error) {
    isLoading.value = false;
    return (serviceValidationErrors.value = (error as any).response._data.inputErrors);
  }
  addToastMessage({ text: t('user.account_requested') });
  if (dynamicFormRef.value) dynamicFormRef.value.reset();
  isLoading.value = false;
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'AccountApplication',
});
</script>
